<template>
  <b-form @submit.prevent="handleSubmit">
    <b-form-group label="Titre *" label-for="ticket-titre">
      <b-form-input
        id="ticket-titre"
        v-model="formData.titre"
        required
        placeholder="Titre du ticket"
      ></b-form-input>
    </b-form-group>

    <b-row>
      <b-col md="6">
        <b-form-group label="Type *" label-for="ticket-type">
          <b-form-select
            id="ticket-type"
            v-model="formData.type"
            required
          >
            <b-form-select-option value="bug">Bug</b-form-select-option>
            <b-form-select-option value="feature">Feature</b-form-select-option>
            <b-form-select-option value="question">Question</b-form-select-option>
            <b-form-select-option value="autre">Autre</b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group label="Priorité *" label-for="ticket-priorite">
          <b-form-select
            id="ticket-priorite"
            v-model="formData.priorite"
            required
          >
            <b-form-select-option value="basse">Basse</b-form-select-option>
            <b-form-select-option value="moyenne">Moyenne</b-form-select-option>
            <b-form-select-option value="haute">Haute</b-form-select-option>
            <b-form-select-option value="urgente">Urgente</b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group label="Projet" label-for="ticket-projet">
      <b-form-input
        id="ticket-projet"
        v-model="formData.projet"
        list="projects-list"
        :disabled="!!parentTicketId"
        placeholder="Choisir un projet existant ou entrer un nouveau nom"
      ></b-form-input>
      <datalist id="projects-list">
        <option v-for="project in projectsList" :key="project" :value="project">{{ project }}</option>
      </datalist>
      <b-form-text v-if="parentTicketId" class="text-muted">
        Le projet est identique à celui du ticket parent.
      </b-form-text>
      <b-form-text v-else-if="projectsList.length === 0" class="text-muted">
        Aucun projet existant. Entrez un nouveau nom de projet.
      </b-form-text>
      <b-form-text v-else class="text-muted">
        {{ projectsList.length }} projet(s) disponible(s). Vous pouvez sélectionner un projet existant ou entrer un nouveau nom.
      </b-form-text>
    </b-form-group>

    <b-form-group label="Assigné à" label-for="ticket-assigned">
      <b-form-select
        id="ticket-assigned"
        v-model="formData.assigned_to"
      >
        <b-form-select-option :value="null">Non assigné</b-form-select-option>
        <optgroup label="SmartMyData">
          <b-form-select-option v-for="user in smdUsersList" :key="user.uid" :value="user.uid">{{ user.text }}</b-form-select-option>
        </optgroup>
        <optgroup v-if="clientUsersList.length" label="Utilisateurs">
          <b-form-select-option v-for="user in clientUsersList" :key="user.uid" :value="user.uid">{{ user.text }}</b-form-select-option>
        </optgroup>
      </b-form-select>
    </b-form-group>

    <b-form-group label="Date de création" label-for="ticket-date">
      <b-form-input
        id="ticket-date"
        v-model="formData.date"
        type="datetime-local"
      ></b-form-input>
      <small class="text-muted">Par défaut : maintenant</small>
    </b-form-group>

    <b-form-group label="Description de la demande *" label-for="ticket-demande">
      <vue-editor
        id="ticket-demande"
        v-model="formData.demande"
        :editorToolbar="toolbar"
        placeholder="Décrivez votre demande en détail..."
      ></vue-editor>
    </b-form-group>

    <b-form-group v-if="isAdmin" label="Cause" label-for="ticket-cause">
      <vue-editor
        id="ticket-cause"
        v-model="formData.cause"
        :editorToolbar="toolbar"
        placeholder="Cause du problème..."
      ></vue-editor>
    </b-form-group>

    <b-form-group v-if="isAdmin" label="Correction" label-for="ticket-correction">
      <vue-editor
        id="ticket-correction"
        v-model="formData.correction"
        :editorToolbar="toolbar"
        placeholder="Correction apportée..."
      ></vue-editor>
    </b-form-group>

    <b-form-group v-if="isAdmin" label="À faire" label-for="ticket-a-faire">
      <vue-editor
        id="ticket-a-faire"
        v-model="formData.a_faire"
        :editorToolbar="toolbar"
        placeholder="Actions à faire..."
      ></vue-editor>
    </b-form-group>

    <b-form-group label="Fichiers (optionnel)">
      <b-form-file
        v-model="formData.files"
        multiple
        placeholder="Choisir des fichiers..."
        drop-placeholder="Déposez les fichiers ici..."
        accept=".jpg,.jpeg,.png,.gif,.pdf,.csv,.txt,.doc,.docx"
      ></b-form-file>
      <small class="text-muted">Max 10 Mo par fichier</small>
    </b-form-group>

    <!-- Info sur ticket parent -->
    <b-alert v-if="parentTicketId" show variant="info" class="mb-3">
      <i class="fas fa-info-circle"></i>
      Ce ticket sera créé comme sous-ticket du ticket <strong>#{{ parentTicketId }}</strong>
    </b-alert>
  </b-form>
</template>

<script>
import { VueEditor } from 'vue2-editor';

export default {
  name: 'CreateTicketForm',
  components: {
    VueEditor
  },
  props: {
    parentTicketId: {
      type: Number,
      default: null
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    clientUid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      toolbar: [
        [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'video'],
        ['clean']
      ],
      formData: {
        titre: '',
        type: 'bug',
        priorite: 'moyenne',
        projet: '',
        demande: '',
        cause: '',
        correction: '',
        a_faire: '',
        statut: 'ouvert',
        assigned_to: null,
        date: this.getCurrentDateTime(),
        files: [],
        ...this.initialData
      },
      usersList: [],
      projectsList: []
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.state.user;
      return !!(user && user.details && user.details.client_uid);
    },
    smdUsersList() {
      return this.usersList.filter(u => u.uid === 'support_smd' || u.is_admin);
    },
    clientUsersList() {
      return this.usersList.filter(u => u.uid !== 'support_smd' && !u.is_admin);
    }
  },
  watch: {
    clientUid(newVal) {
      this.formData.projet = '';
      this.loadProjects();
    },
    initialData: {
      handler(newVal) {
        this.formData = {
          titre: '',
          type: 'bug',
          priorite: 'moyenne',
          projet: '',
          demande: '',
          cause: '',
          correction: '',
          a_faire: '',
          statut: 'ouvert',
          assigned_to: null,
          date: this.getCurrentDateTime(),
          files: [],
          ...newVal
        };
      },
      deep: true
    }
  },
  mounted() {
    this.loadUsers();
    this.loadProjects();
  },
  methods: {
    getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    async loadUsers() {
      try {
        const ticketService = (await import('../services/tickets')).default;
        const data = await ticketService.listUsers();
        this.usersList = data.users || [];
      } catch (error) {
        console.error('Error loading users:', error);
        this.usersList = [];
      }
    },
    async loadProjects() {
      try {
        const ticketService = (await import('../services/tickets')).default;
        const data = await ticketService.listProjects(this.clientUid || null);
        this.projectsList = data.projects || [];
      } catch (error) {
        console.error('Error loading projects:', error);
        this.projectsList = [];
      }
    },
    handleSubmit() {
      this.$emit('submit', {
        ...this.formData,
        parent_ticket: this.parentTicketId
      });
    },
    reset() {
      this.formData = {
        titre: '',
        type: 'bug',
        priorite: 'moyenne',
        projet: '',
        demande: '',
        cause: '',
        correction: '',
        a_faire: '',
        statut: 'ouvert',
        assigned_to: null,
        date: this.getCurrentDateTime(),
        files: []
      };
    },
    getData() {
      return {
        ...this.formData,
        parent_ticket: this.parentTicketId
      };
    }
  }
};
</script>

<style scoped>
/* Styles spécifiques au formulaire si nécessaire */
</style>
